import { useCallback } from 'react';
import { Menu } from '@mui/material';
import Actions, { MenuAction } from '@/components/Menu/Actions';
import { color } from '@/colors';
import type { MenuProps } from '@mui/material';

interface AppContextMenuProps extends Omit<MenuProps, 'onClose'> {
  header?: React.ReactNode;
  items?: MenuAction[];
  maxHeight?: string;
  mouseX?: number | null;
  mouseY?: number | null;
  noClose?: boolean;
  onClose: (e: React.MouseEvent) => void;
  width?: string;
  offset?: { top?: string; left?: string };
  slotProps?: MenuProps['slotProps'];
}

export const AppContextMenu = ({
  children,
  header,
  items,
  mouseX,
  mouseY,
  noClose = false,
  onClose,
  width,
  maxHeight,
  offset,
  onKeyDown,
  slotProps,
  ...props
}: AppContextMenuProps) => {
  // On Arrow Up/Down, focus the next/previous item relative to the hovered item or the first/last item if no item is hovered, we are querying the dom directly because currently AppContextMenu is not aware of its children, so we can't manage it on that level
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.stopPropagation();

      const activeElement = document.activeElement;
      const menuElement = e.currentTarget;
      const menuItems = Array.from(menuElement.querySelectorAll('li'));

      if (menuItems.length === 0) return;

      const isMenuItemFocused = menuItems.some((item) => item === activeElement);

      if (!isMenuItemFocused) {
        // No item is focused - check if mouse is hovering over an item
        const hoveredItem = menuItems.find((item) => item.matches(':hover'));

        if (hoveredItem) {
          // Mouse is hovering - focus the next/previous item relative to hovered item
          const hoveredIndex = menuItems.indexOf(hoveredItem);
          let targetIndex: number;

          if (e.key === 'ArrowDown') {
            targetIndex = hoveredIndex + 1 >= menuItems.length ? 0 : hoveredIndex + 1;
          } else {
            targetIndex = hoveredIndex - 1 < 0 ? menuItems.length - 1 : hoveredIndex - 1;
          }

          const targetItem = menuItems[targetIndex];
          if (targetItem instanceof HTMLElement) {
            targetItem.focus();
          }
        } else {
          // No hover - fall back to first/last item
          const targetItem = e.key === 'ArrowDown' ? menuItems[0] : menuItems[menuItems.length - 1];
          if (targetItem instanceof HTMLElement) {
            targetItem.focus();
          }
        }
      }
    }
    onKeyDown?.(e as React.KeyboardEvent<HTMLDivElement>);
  };

  const handleClose = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onClose(e);
    },
    [onClose],
  );

  const handleCloseActions = useCallback(
    (e: React.MouseEvent) => {
      if (noClose) {
        return;
      }
      onClose(e);
    },
    [noClose, onClose],
  );

  return (
    <Menu
      {...props}
      anchorReference={props.anchorEl ? 'anchorEl' : 'anchorPosition'}
      disableRestoreFocus // DO NOT REMOVE: this fixes the issue of viewport jumps when menus close due to the built-in scroll into view behavior WEA-2353
      open={props.open}
      onClose={handleClose}
      onKeyDown={handleKeyDown}
      anchorPosition={props.open ? { top: mouseY || 0, left: mouseX || 0 } : undefined}
      slotProps={{
        paper: {
          sx: {
            [width ? 'width' : 'minWidth']: width || '240px',
            background: color.Black92,
            border: `1px solid ${color.White04_T}`,
            borderRadius: 2,
            px: 0.5,
            maxHeight: maxHeight,
            ...(offset && {
              mt: offset.top,
              ml: offset.left,
            }),
          },
        },
        list: {
          sx: {
            py: 0.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
          },
        },
        ...slotProps,
      }}
    >
      {header}
      {items ? <Actions items={items} onClose={handleCloseActions} /> : children}
    </Menu>
  );
};
